// Fixed SettingsPage.jsx with proper state synchronization
import React, { useState, useEffect, useCallback } from "react";
import { Settings, Lock, Zap, Globe, Brain, TrendingUp, Shield, Users, CheckCircle, RefreshCw } from "lucide-react";
import api from "../utils/api.js";
import { useAuth } from "../utils/auth";

export default function SettingsPage() {
  // Auth context for triggering refreshes
  const { user, refreshUser } = useAuth();
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [userPlan, setUserPlan] = useState("Professional");
  const [analysisCount, setAnalysisCount] = useState(847);
  const [monthlyLimit, setMonthlyLimit] = useState(0);
  
  // AI Analysis Settings
  const [aiSettings, setAiSettings] = useState({
    enablePredictiveAnalytics: true,
    enableCompetitiveIntelligence: true,
    enableIndustryBenchmarking: true,
    autoAnalysisFrequency: "weekly",
    analysisDepth: "comprehensive"
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    newOpportunities: true,
    competitorUpdates: false,
    performanceAlerts: true,
    weeklyReports: true
  });

  // 🔥 ENHANCED: Fetch all settings data
  const fetchSettings = useCallback(async () => {
    try {
      setPageLoading(true);
      console.log('🔄 Fetching settings data...');
      
      const response = await api.get("/users/settings");
      console.log('✅ Settings response:', response.data);
      
      const { plan, usage, preferences } = response.data;
      
      // Update state with fresh data
      setUserPlan(plan);
      setAnalysisCount(usage.analysisCount);
      setMonthlyLimit(usage.monthlyLimit);
      
      if (preferences.ai) {
        setAiSettings(preferences.ai);
      }
      
      if (preferences.notifications) {
        setNotifications(preferences.notifications);
      }
      
      setMessage("Settings loaded successfully");
      setTimeout(() => setMessage(""), 3000);
      
    } catch (err) {
      console.error("❌ Error fetching settings:", err);
      setMessage("Error loading settings. Please refresh the page.");
    } finally {
      setPageLoading(false);
    }
  }, []);

  // Load settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // 🔥 ENHANCED: Password change with better feedback
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage("");

    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setMessage("New password must be at least 6 characters long.");
      return;
    }

    setLoading(true);
    try {
      console.log('🔒 Changing password...');
      
      await api.post("/users/change-password", {
        currentPassword,
        newPassword,
      });
      
      console.log('✅ Password changed successfully');
      setMessage("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      // Trigger auth refresh if available
      if (refreshUser) {
        await refreshUser();
      }
      
    } catch (err) {
      console.error("❌ Password change error:", err);
      setMessage(err.response?.data?.detail || err.response?.data?.message || "Error changing password.");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 ENHANCED: AI settings update with state sync
  const handleAiSettingsUpdate = async (newSettings) => {
    try {
      setLoading(true);
      console.log('🤖 Updating AI settings:', newSettings);
      
      await api.put("/users/settings/ai", newSettings);
      console.log('✅ AI settings updated successfully');
      
      // Update local state immediately
      setAiSettings(newSettings);
      setMessage("AI preferences updated successfully.");
      
      // Refresh user data in auth context
      if (refreshUser) {
        await refreshUser();
      }
      
      // Optional: Refresh all settings to ensure sync
      setTimeout(() => {
        fetchSettings();
      }, 1000);
      
    } catch (err) {
      console.error("❌ AI settings update error:", err);
      setMessage("Error updating AI settings.");
      // Revert local state on error
      await fetchSettings();
    } finally {
      setLoading(false);
    }
  };

  // 🔥 ENHANCED: Notification update with state sync
  const handleNotificationUpdate = async (newNotifications) => {
    try {
      setLoading(true);
      console.log('📧 Updating notifications:', newNotifications);
      
      await api.put("/users/settings/notifications", newNotifications);
      console.log('✅ Notifications updated successfully');
      
      // Update local state immediately
      setNotifications(newNotifications);
      setMessage("Notification preferences updated.");
      
      // Refresh user data in auth context
      if (refreshUser) {
        await refreshUser();
      }
      
      // Optional: Refresh all settings to ensure sync
      setTimeout(() => {
        fetchSettings();
      }, 1000);
      
    } catch (err) {
      console.error("❌ Notification update error:", err);
      setMessage("Error updating notification settings.");
      // Revert local state on error
      await fetchSettings();
    } finally {
      setLoading(false);
    }
  };

  // 🔥 ENHANCED: Manual refresh function
  const handleRefreshSettings = async () => {
    setMessage("Refreshing settings...");
    await fetchSettings();
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case "Observer": return "text-gray-600";
      case "Professional": return "text-blue-600";
      case "Business": return "text-purple-600";
      case "Enterprise": return "text-amber-600";
      default: return "text-gray-600";
    }
  };

  const getPlanIcon = (plan) => {
    switch (plan) {
      case "Observer": return <Globe className="w-5 h-5" />;
      case "Professional": return <Brain className="w-5 h-5" />;
      case "Business": return <TrendingUp className="w-5 h-5" />;
      case "Enterprise": return <Shield className="w-5 h-5" />;
      default: return <Settings className="w-5 h-5" />;
    }
  };

  // Loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-700 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-800 rounded w-2/3"></div>
            </div>
          </div>
          <div className="animate-pulse space-y-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/10 rounded-lg p-6 h-48"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Refresh Button */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
              <p className="text-gray-300">Manage your CodeRated account and AI preferences</p>
            </div>
            <button
              onClick={handleRefreshSettings}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Enhanced Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg border flex items-center gap-2 ${
            message.includes("Error") || message.includes("not match") || message.includes("must be")
              ? "bg-red-900/20 border-red-500/30 text-red-400" 
              : message.includes("successfully") || message.includes("loaded")
              ? "bg-green-900/20 border-green-500/30 text-green-400"
              : "bg-blue-900/20 border-blue-500/30 text-blue-400"
          }`}>
            {message.includes("successfully") || message.includes("loaded") ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <Settings className="w-5 h-5" />
            )}
            {message}
          </div>
        )}

        <div className="grid gap-6">
          {/* Plan Overview with Real-time Data */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`${getPlanColor(userPlan)}`}>
                  {getPlanIcon(userPlan)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">{userPlan} Plan</h2>
                  <p className="text-gray-300 text-sm">AI-powered website intelligence</p>
                </div>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Upgrade Plan
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-black/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">{analysisCount.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Analyses This Month</div>
              </div>
              <div className="bg-black/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">
                  {monthlyLimit === 0 ? "Unlimited" : monthlyLimit.toLocaleString()}
                </div>
                <div className="text-gray-300 text-sm">Monthly Limit</div>
              </div>
              <div className="bg-black/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-400">98.7%</div>
                <div className="text-gray-300 text-sm">AI Accuracy Score</div>
              </div>
            </div>
          </div>

          {/* AI Intelligence Settings with Better State Management */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="w-6 h-6 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">AI Intelligence Settings</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Predictive Analytics</h3>
                  <p className="text-gray-400 text-sm">Future performance forecasting and trend analysis</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiSettings.enablePredictiveAnalytics}
                    onChange={(e) => handleAiSettingsUpdate({
                      ...aiSettings,
                      enablePredictiveAnalytics: e.target.checked
                    })}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 disabled:opacity-50"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Industry Benchmarking</h3>
                  <p className="text-gray-400 text-sm">Compare against industry standards and leaders</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiSettings.enableIndustryBenchmarking}
                    onChange={(e) => handleAiSettingsUpdate({
                      ...aiSettings,
                      enableIndustryBenchmarking: e.target.checked
                    })}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 disabled:opacity-50"></div>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <label className="block text-white font-medium mb-2">Analysis Frequency</label>
                  <select
                    value={aiSettings.autoAnalysisFrequency}
                    onChange={(e) => handleAiSettingsUpdate({
                      ...aiSettings,
                      autoAnalysisFrequency: e.target.value
                    })}
                    disabled={loading}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="manual">Manual Only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Analysis Depth</label>
                  <select
                    value={aiSettings.analysisDepth}
                    onChange={(e) => handleAiSettingsUpdate({
                      ...aiSettings,
                      analysisDepth: e.target.value
                    })}
                    disabled={loading}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
                  >
                    <option value="quick">Quick Scan</option>
                    <option value="standard">Standard Analysis</option>
                    <option value="comprehensive">Comprehensive Review</option>
                    <option value="deep-dive">Deep Intelligence</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-6 h-6 text-red-400" />
              <h2 className="text-xl font-semibold text-white">Security</h2>
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-white font-medium mb-2">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
                    placeholder="Enter new password"
                    required
                    minLength="6"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
                    placeholder="Confirm new password"
                    required
                    minLength="6"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Updating..." : "Change Password"}
              </button>
            </form>
          </div>

          {/* Notification Preferences with Better State */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-semibold text-white">Notifications</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">New Opportunities</h3>
                  <p className="text-gray-400 text-sm">Get alerted when we discover improvement opportunities</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.newOpportunities}
                    onChange={(e) => handleNotificationUpdate({
                      ...notifications,
                      newOpportunities: e.target.checked
                    })}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600 disabled:opacity-50"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Competitor Updates</h3>
                  <p className="text-gray-400 text-sm">Notifications when competitors make significant changes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.competitorUpdates}
                    onChange={(e) => handleNotificationUpdate({
                      ...notifications,
                      competitorUpdates: e.target.checked
                    })}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600 disabled:opacity-50"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Performance Alerts</h3>
                  <p className="text-gray-400 text-sm">Critical performance issues and improvements</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.performanceAlerts}
                    onChange={(e) => handleNotificationUpdate({
                      ...notifications,
                      performanceAlerts: e.target.checked
                    })}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600 disabled:opacity-50"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">Weekly Reports</h3>
                  <p className="text-gray-400 text-sm">Comprehensive weekly intelligence reports</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.weeklyReports}
                    onChange={(e) => handleNotificationUpdate({
                      ...notifications,
                      weeklyReports: e.target.checked
                    })}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600 disabled:opacity-50"></div>
                </label>
              </div>
            </div>
          </div>

          {/* API & Integrations */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">API & Integrations</h2>
            </div>
            
            <div className="space-y-4">
              <div className="bg-black/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium">API Access</h3>
                  <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">Active</span>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Access CodeRated's intelligence through our REST API
                </p>
                <div className="flex items-center gap-4">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm">
                    Generate Key
                  </button>
                  <button className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm">
                    View Docs
                  </button>
                </div>
              </div>

              <div className="bg-black/20 rounded-lg p-4">
                <h3 className="text-white font-medium mb-2">White-Label Solutions</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Integrate CodeRated intelligence into your own platform
                </p>
                <button className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-sm">
                  Contact Sales
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}